import { useCallback, useRef, useState, type FC } from 'react';

import type { MultiSelectOption } from '@app/shared/components/MultiSelect/types';
import { MultiSelectOptionRow } from '@app/shared/components/MultiSelect/ui/MultiSelectOptionRow';

const PF = 'multi-select';
const OPTION_ROW_HEIGHT = 34;
const OPTION_LIST_HEIGHT = 220;
const OPTION_LIST_OVERSCAN = 6;

interface MultiSelectVirtualListProps {
  options: MultiSelectOption[];
  selectedValueSet: Set<string>;
  optionDataTestId: string;
  activeCheckboxDataTestId: string;
  deactivateCheckboxDataTestId: string;
  onToggle(value: string, checked: boolean): void;
  renderOptionLabel(option: MultiSelectOption): string;
}

export const MultiSelectVirtualList: FC<MultiSelectVirtualListProps> = ({
  options,
  selectedValueSet,
  optionDataTestId,
  activeCheckboxDataTestId,
  deactivateCheckboxDataTestId,
  onToggle,
  renderOptionLabel,
}) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const scrollHandler = useCallback(() => {
    setScrollTop(listRef.current?.scrollTop ?? 0);
  }, []);

  if (options.length === 0) {
    return (
      <div className={`${PF}__group__list`}>
        <div className={`${PF}__empty`} data-testid="no-data">
          Нет результатов
        </div>
      </div>
    );
  }

  const totalHeight = options.length * OPTION_ROW_HEIGHT;
  const visibleRowsCount = Math.ceil(OPTION_LIST_HEIGHT / OPTION_ROW_HEIGHT);
  const maxStartIndex = Math.max(0, options.length - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(
      0,
      Math.floor(scrollTop / OPTION_ROW_HEIGHT) - OPTION_LIST_OVERSCAN
    )
  );
  const endIndex = Math.min(
    options.length,
    startIndex + visibleRowsCount + OPTION_LIST_OVERSCAN * 2
  );
  const visibleOptions = options.slice(startIndex, endIndex);
  const offsetTop = startIndex * OPTION_ROW_HEIGHT;

  return (
    <div
      className={`${PF}__group__list`}
      ref={listRef}
      onScroll={scrollHandler}
    >
      <div
        className={`${PF}__group__virtual-spacer`}
        style={{ height: totalHeight }}
      >
        <div
          className={`${PF}__group__virtual-items`}
          style={{ transform: `translateY(${offsetTop}px)` }}
        >
          {visibleOptions.map((option) => (
            <MultiSelectOptionRow
              key={option.value}
              option={option}
              checked={selectedValueSet.has(option.value)}
              optionDataTestId={optionDataTestId}
              activeCheckboxDataTestId={activeCheckboxDataTestId}
              deactivateCheckboxDataTestId={deactivateCheckboxDataTestId}
              onToggle={onToggle}
              renderOptionLabel={renderOptionLabel}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
